import React, { useCallback, useMemo } from "react";
import { times } from "lodash";
import MarkDown from 'react-markdown';
import accounting from 'accounting';
import moment from "moment";
import { PleaseWaitSpinner } from "ts-common-react-controls/lib/module/waitspinner";
import TranslateManager from 'ts-translate-js';
import { PaymentInfoModel } from "../../../services/orderprocessing";

const tr = TranslateManager.boundTR;

type RenewalPaymentProps = {
    total: number,
    transactionInProgress: boolean,
    orderPrepareResult: any,
    orderProcessingResult: any,
    onCompleteTransaction: (paymentInfo: PaymentInfoModel) => void
};

export function RenewalPayment({total, transactionInProgress, orderPrepareResult, orderProcessingResult, onCompleteTransaction}: RenewalPaymentProps) {
    const [paymentInfo, setPaymentInfo] = React.useState({
        Method:"creditcard",
        CardHolderName:"",
        CardHolderPostalCode: "",
        CardHolderCountry: "",
        CardHolderAddress1: "",
        CardNumber:"",
        CardExpMonth: "01",
        CardExpYear: moment().year(),
        CVNumber:"",
    });

    const handleOnChange = useCallback((e, name) => {
        const change = Object.assign({}, paymentInfo, { [name]: e.target.value });
        setPaymentInfo(change);
    }, [setPaymentInfo, paymentInfo]);

    const fillExpirationMonth = () => {
        const months = ["01","02","03","04","05","06","07","08","09","10","11","12"];
        return (months.map(function(month){
            return (<option key={month} className="text-center">{month}</option>);
        }));
    };

    const fillExpirationYear = () => {
        let years = [], curYear;

        curYear = moment().year();

        times(15, (i) => years.push(curYear + i));

        return (years.map(function(year){
            return (<option key={year} className="text-center">{year}</option>);
        }));
    };

    const paymentErrorMessages = useMemo(() => {
        const retMessages = [];

        if (!transactionInProgress && orderPrepareResult != null && !orderPrepareResult.isSuccess)
        {
            const messages = [];

            if (orderPrepareResult.acceptDisclaimers && orderPrepareResult.acceptPolicies)
                messages.push(tr("Vous devez accepter les décharges de responsabilités et les politiques d'achats"));
            else {
                if (orderPrepareResult.acceptPolicies)
                    messages.push(tr("Vous devez accepter les politiques d'achats"));
                if (orderPrepareResult.acceptDisclaimers)
                    messages.push(tr("Vous devez accepter les décharges de responsabilités"));
            }

            if (orderPrepareResult.billingAddressErrors.length > 0)
                 messages.push(tr("Vous devez entrer vos coordonnées"));

            if(messages.length === 0)
                messages.push(tr("payment.failedprocess"));

            messages.forEach((message, index) => {
                retMessages.push(<div key={index} className="alert alert-danger col-sm-8 text-center" role="alert">{message}</div>);
            });
        }
        else if (orderProcessingResult != null && orderProcessingResult.CreditCardConditionNotMet)
            retMessages.push(<div className="alert alert-danger col-sm-8 text-center" role="alert"><MarkDown className="markdown-inline" source={tr("payment.creditcardconditionnotmet", {creditcard: orderProcessingResult.CreditCardConditionName})} /></div>);
        else if (orderProcessingResult != null && !orderProcessingResult.IsSuccess && orderProcessingResult.HasMessage)
            retMessages.push(<div className="alert alert-danger col-sm-8 text-center" role="alert">{orderProcessingResult.Message}</div>);
        else if (orderProcessingResult != null && !orderProcessingResult.IsSuccess && orderProcessingResult.RedirectCheckoutError)
            retMessages.push(<div className="alert alert-danger col-sm-8 text-center" role="alert">{tr("Une erreur est survenue")}</div>);

        return retMessages;
    }, [transactionInProgress, orderPrepareResult, orderProcessingResult]);

    const handleCompleteTransaction = useCallback((paymentMethod) => {
        onCompleteTransaction(paymentMethod);
    }, [onCompleteTransaction]);

    const payment = useMemo(() => {
        let processButtonSection;
        if(transactionInProgress) {
            processButtonSection = <div className="col-md-4 col-md-offset-1"><PleaseWaitSpinner size="md"/></div>;
        }
        else {
            let amountBadge = null;
            if (total > 0) amountBadge = <span className="badge accented-text">{accounting.formatMoney(total)}</span>;
            processButtonSection = <div className="col-sm-8">
                <button type="submit" className="btn btn-primary btn-lg"> <span className="glyphicon glyphicon-lock"></span> {tr("payment.completetransaction")} {amountBadge}</button>
            </div>;
        }

        let paymentPanel = null;
        if (total == 0) {
            paymentPanel = <form className="form-horizontal" onSubmit={(e) => { e.preventDefault(); handleCompleteTransaction(paymentInfo); }}>
                {paymentErrorMessages}
                <div className="form-group">
                    {processButtonSection}
                </div>
            </form>;
        } else {
            paymentPanel = <form className="form-horizontal" onSubmit={(e) => { e.preventDefault(); handleCompleteTransaction(paymentInfo); }}>
                <div className="form-group">
                    <div className="col-sm-3">
                        <div className = "cardPaymentAvailable visa"></div>
                        <div className = "cardPaymentAvailable mastercard"></div>
                    </div>
                </div>
                <div className="form-group">
                    <label htmlFor="inputCardHolderName" className="col-sm-3 control-label left">{tr("payment.cardholdername")}</label>
                    <div className="col-sm-3">
                        <input type="text" id="inputCardHolderName" className="form-control" maxLength={150} onChange={(e) => handleOnChange(e,"CardHolderName")}></input>
                    </div>
                </div>
                <div className="form-group">
                    <label htmlFor="inputCardNumber" className="col-sm-3 control-label left">{tr("payment.cardnumber")}<p className="help-block">{tr("payment.cardnumberhelp")}</p></label>
                    <div className="col-sm-3">
                        <input type="text" id="inputCardNumber" className="form-control" maxLength={20} onChange={(e) => handleOnChange(e,"CardNumber")}></input>
                    </div>
                </div>
                <div className="form-group">
                    <label htmlFor="inputCVN" className="col-sm-3 col-xs-12 control-label left">{tr("payment.cardvalidationnumber")}<p className="help-block">{tr("payment.cvnhelp")}</p></label>
                    <div className="col-sm-1 col-xs-5">
                        <input type="text" id="inputCVN" className="form-control col-sm-1" maxLength={4} onChange={(e) => handleOnChange(e,"CVNumber")}></input>
                    </div>
                </div>
                <div className="form-group">
                    <label className="col-sm-3 control-label left">{tr("payment.expirationdate")}</label>
                    <div className="btn-toolbar col-sm-3">
                        <div className="btn-group">
                            <select className="form-control" onChange={(e) => handleOnChange(e,"CardExpMonth")}>
                                {fillExpirationMonth()}
                            </select>
                        </div>
                        <div className="btn-group">
                            <select className="form-control" onChange={(e) => handleOnChange(e,"CardExpYear")}>
                                {fillExpirationYear()}
                            </select>
                        </div>
                    </div>
                </div>
                {paymentErrorMessages}
                <div className="form-group">
                    {processButtonSection}
                </div>
            </form>;
        }
                                        
        return <div className="well payment-container">
            <div className="form-horizontal">
                <div className="form-group">
                    <label className="col-xs-6 col-sm-3 left">{tr("Montant à payer")}</label>
                    <div className="col-xs-4 col-sm-2">
                        <div className="amount">
                            <b>{accounting.formatMoney(total)}</b>
                        </div>
                    </div>
                </div>
                { total > 0 ? <hr /> : null }
                { paymentPanel }
            </div>
        </div>;
    },[total, transactionInProgress, handleOnChange, handleCompleteTransaction, paymentErrorMessages, paymentInfo]);

    return payment;
}